import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { EntryEntity } from '../../constants/entries';
import EntriesView from '../matterDetail/entries/entriesView';
import { NotesEntity } from '../../constants/notes';
import Notes from '../conflictSearch/notes';
import { useFetchPreliminaryInvoiceById } from '../../hooks/useFetchPreliminaryInvoiceById';
import { IPreliminaryInvoice, MyInvoicingDetailTab, MyInvoicingTab } from '../../interface/invoice';
import PreliminaryInvoiceGeneralCard from './preliminaryInvoiceGeneralCard';
import PreliminaryInvoicePayersTab from '../parties/preliminaryInvoicePayersTab';
import { useUser } from '../../context/UserContext';
import CustomModal from '../informationPopup/customModal';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomTabs from '../customTabs/customTabs';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';
import { useSystemInfo } from '../../context/SystemInfoContext';

const PreliminaryInvoiceTabView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useUser();
  const location = useLocation();
  const { goBack, updateTop } = useNavigationStack();
  const { setTitle } = usePage();
  const { trackPage } = useAnalytics();
  const { systemParams } = useSystemInfo();
  const isPreInvoiceV2 = systemParams?.featureFlags?.preliminaryInvoice?.version === 'v2';
  const fromTab = location.state?.fromTab || MyInvoicingDetailTab.general;
  const fromApprovalNotification = location.state?.fromApprovalNotification || false;
  const [showPreInvoiceNotAssignedToUserDialog, setShowPreInvoiceNotAssignedToUserDialog] =
    useState<boolean>(false);
  const [showApprovalInformationDialog, setShowApprovalInformationDialog] =
    useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<string>(fromTab);
  const {
    data: preliminaryInvoice,
    isLoading,
    isError,
  } = useFetchPreliminaryInvoiceById({
    id,
    relations: [
      'matter:subject,matterNumber,currency,invoiceableType',
      'company:name',
      'privatePerson:fullName',
    ],
    fields: [
      'status',
      'entryDate',
      isPreInvoiceV2 ? 'totalNetAmount' : 'sumOfBillableEntries',
      isPreInvoiceV2 ? 'totalNetAmountInMatterCurrency' : 'sumOfBillableEntriesInCurrency',
      'periodStartDate',
      'periodEndDate',
      'reviewStatus',
      'assignToUserId',
      'hasImportantNotes',
      'hasImportantRelatedNotes',
      'hasNotes',
    ],
  });
  useEffect(() => {
    updateTop({ fromTab: selectedTab });
  }, [selectedTab]);
  useEffect(() => {
    trackPage(AnalyticsPage.MY_INVOICING_LISTING);
  }, [trackPage]);

  const handleBack = () => {
    goBack({ fromTab: MyInvoicingTab.preliminaryInvoices });
  };
  useEffect(() => {
    if (preliminaryInvoice && !isLoading) {
      setTitle(
        <AddEditTransactionHeader
          title={preliminaryInvoice?.matter?.subject ?? ''}
          subTitle={t('preliminaryInvoice:myPreliminaryInvoice')}
          handleBack={handleBack}
        />,
      );
    }
  }, [preliminaryInvoice, isLoading]);
  useEffect(() => {
    if (preliminaryInvoice && preliminaryInvoice.assignToUserId !== user?.id) {
      setShowPreInvoiceNotAssignedToUserDialog(true);
    }
    if (fromApprovalNotification && preliminaryInvoice && !isError) {
      setShowApprovalInformationDialog(true);
    }
  }, [preliminaryInvoice, isError]);

  const items = useMemo(
    () => [
      {
        key: MyInvoicingDetailTab.general,
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <PreliminaryInvoiceGeneralCard
              preliminaryInvoice={preliminaryInvoice as IPreliminaryInvoice}
              key={preliminaryInvoice?.id}
              isLoading={isLoading}
            />
          </DetailScrollArea>
        ),
      },
      {
        key: MyInvoicingDetailTab.billableEntries,
        label: t('common:tab.billableEntries'),
        children: <EntriesView entity={EntryEntity.INVOICE} />,
      },
      {
        key: MyInvoicingDetailTab.payers,
        label: t('common:tab.payers'),
        children: <PreliminaryInvoicePayersTab />,
      },
      {
        key: MyInvoicingDetailTab.notes,
        label: t('common:tab.notes'),
        children: (
          <Notes
            entitySubject={preliminaryInvoice?.matter?.subject ?? ''}
            entity={NotesEntity.PRELIMINARYINVOICE}
          />
        ),
      },
    ],
    [preliminaryInvoice, t],
  );
  return (
    <>
      <CustomTabs variant="pill" activeKey={selectedTab} onChange={setSelectedTab} items={items} />
      <CustomModal
        visible={showPreInvoiceNotAssignedToUserDialog}
        onConfirm={() => {
          setShowPreInvoiceNotAssignedToUserDialog(false);
          goBack();
        }}
        confirmText={t('common:ok')}
        title={t('preliminaryInvoice:preliminaryInvoiceNotAssignedToUserMessage')}
      />
      <CustomModal
        visible={showApprovalInformationDialog}
        onConfirm={() => {
          setShowApprovalInformationDialog(false);
          goBack();
        }}
        confirmText={t('common:ok')}
        title={t('preliminaryInvoice:preliminaryInvoiceApprovalInformativeMessage')}
      />
    </>
  );
};

export default PreliminaryInvoiceTabView;
